import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Wallet, Fuel, Ban, Paperclip, X, ArrowDownLeft, ArrowUpRight, Printer, ListTree } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { openPdf } from "@/lib/print";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import BankAccountSelect from "@/components/BankAccountSelect";

import { fmtDate } from "@/lib/format";
import { confirmAction } from "@/lib/dialogs";
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const NO_ACTIVITY = "__none__";
const activityLabel = (activities, id) => {
  if (!id) return "—";
  const a = activities.find((w) => w.id === id);
  return a ? `${a.code} — ${a.name}` : "—";
};

function ActivitySelect({ value, onChange, activities }) {
  return (
    <Select value={value || NO_ACTIVITY} onValueChange={(v) => onChange(v === NO_ACTIVITY ? "" : v)}>
      <SelectTrigger className="mt-1.5" data-testid="site-expense-activity"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_ACTIVITY}>No activity (project cost only)</SelectItem>
        {activities.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function AssignActivityDialog({ expense, projectId, activities, onClose, onDone }) {
  const [wbs, setWbs] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setWbs(expense?.wbs_item_id || ""); }, [expense]);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/site-expenses/${expense.id}/activity`, { wbs_item_id: wbs || null });
      toast.success(wbs ? "Expense assigned to the activity" : "Activity removed from the expense");
      onDone();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!expense} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Assign to an activity</DialogTitle></DialogHeader>
        {expense && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{expense.description} — <span className="font-mono">{inr(expense.total)}</span></p>
            <div><Label>Activity</Label><ActivitySelect value={wbs} onChange={setWbs} activities={activities} /></div>
            <p className="text-[11px] text-muted-foreground">Only the activity this cost counts against in Budget vs Actual changes. The expense and the accounts are not touched.</p>
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="assign-activity-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function openAttachment(path) {
  const tab = window.open("", "_blank");
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    if (tab) tab.location.href = url; else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (tab) tab.close();
    toast.error("Could not open the bill photo");
  }
}

export default function SiteExpenses() {
  const { projectId } = useProjectCtx();
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [tab, setTab] = useState("expenses");
  const [loading, setLoading] = useState(false);
  const [cashRequestData, setCashRequestData] = useState({ requests: [], can_request: false, can_approve: false, can_issue: false });
  const [issuingRequest, setIssuingRequest] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: res }, wbs, cashRequests] = await Promise.all([
        api.get(`/projects/${projectId}/site-expenses`),
        api.get(`/projects/${projectId}/wbs`, { params: { include_groups: false } }).then((r) => r.data).catch(() => []),
        api.get(`/projects/${projectId}/petty-cash-requests`).then((r) => r.data),
      ]);
      setData(res);
      setActivities(wbs);
      setCashRequestData(cashRequests);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (projectId) load(); }, [projectId]);

  const cancelExpense = async (e) => {
    const reason = window.prompt(`Cancel "${e.description}" (${inr(e.total)})? Give a reason:`);
    if (!reason) return;
    try {
      await api.post(`/projects/${projectId}/site-expenses/${e.id}/cancel`, { reason });
      toast.success("Expense cancelled — cost and accounts reversed");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const cancelPetty = async (p) => {
    if (!await confirmAction(`Cancel this petty cash ${p.kind === "TOPUP" ? "top-up" : "return"} of ${inr(p.amount)}?`)) return;
    try {
      await api.post(`/projects/${projectId}/petty-cash/${p.id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const cancelFuel = async (f) => {
    if (!await confirmAction(`Cancel this Fuel Account ${f.kind === "TOPUP" ? "top-up" : "return"} of ${inr(f.amount)}?`)) return;
    try {
      await api.post(`/projects/${projectId}/fuel-account/${f.id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const reviewCashRequest = async (request, action) => {
    const verb = action === "approve" ? "Approve" : "Reject";
    if (!await confirmAction(`${verb} the petty cash request for ${inr(request.requested_amount)} for ${request.recipient}?`)) return;
    try {
      await api.post(`/projects/${projectId}/petty-cash-requests/${request.id}/${action}`, { note: null });
      toast.success(action === "approve" ? "Petty cash request approved" : "Petty cash request rejected");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!data) return <p className="text-sm text-muted-foreground">{loading ? "Loading…" : ""}</p>;
  const balance = Number(data.petty_balance);
  const fuelBalance = Number(data.fuel_balance);
  const cashRequests = cashRequestData.requests.filter((request) => request.status !== "ISSUED");

  return (
    <div className="space-y-5" data-testid="site-expenses-page">
      <PageHeader
        title="Site Expenses"
        description="Diesel, food, transport, repairs, daily wages and other small spends at this site. One entry updates the project cost and the company accounts together."
        action={<div className="flex flex-wrap gap-2">
          {data.can_manage && <PettyCashDialog projectId={projectId} companyId={data.company_id} balance={balance} canIssue={data.can_issue_petty_cash} onSaved={load} />}
          {data.can_manage && <FuelAccountDialog projectId={projectId} companyId={data.company_id} balance={fuelBalance} onSaved={load} />}
          {data.can_enter && <ExpenseDialog projectId={projectId} companyId={data.company_id} categories={data.categories} activities={activities} balance={balance} onSaved={load} />}
          {cashRequestData.can_request && <PettyCashRequestDialog projectId={projectId} onSaved={load} />}
        </div>}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Petty cash at site</p>
          <p className={`mt-2 font-mono text-lg font-bold ${balance < 0 ? "text-rose-500" : balance < 10000 ? "text-amber-600 dark:text-amber-400" : "text-foreground"}`}>{inr(balance)}</p>
          {balance < 10000 && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Below the ₹10,000 minimum — request a top-up</p>}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fuel Account at site</p>
          <p className={`mt-2 font-mono text-lg font-bold ${fuelBalance < 0 ? "text-rose-500" : fuelBalance < 2000 ? "text-amber-600 dark:text-amber-400" : "text-foreground"}`}>{inr(fuelBalance)}</p>
          {fuelBalance < 2000 && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Running low</p>}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Spent this month</p>
          <p className="mt-2 font-mono text-lg font-bold text-foreground">{inr(data.spent_this_month)}</p>
        </div>
        <div className="col-span-2 rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">By category (total {inr(data.spent_total)})</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {data.by_category.length === 0 ? <span className="text-muted-foreground">No expenses yet</span> : data.by_category.map((c) => (
              <span key={c.category}><span className="text-muted-foreground">{c.label}</span> <span className="font-mono text-foreground">{inr(c.amount)}</span></span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button size="sm" variant={tab === "expenses" ? "default" : "outline"} onClick={() => setTab("expenses")}>Expenses ({data.expenses.length})</Button>
        <Button size="sm" variant={tab === "petty" ? "default" : "outline"} onClick={() => setTab("petty")}>Petty cash given / returned ({data.petty_cash.length})</Button>
        <Button size="sm" variant={tab === "requests" ? "default" : "outline"} onClick={() => setTab("requests")}>Cash requests ({cashRequests.length})</Button>
        <Button size="sm" variant={tab === "fuel" ? "default" : "outline"} onClick={() => setTab("fuel")}>Fuel Account given / returned ({data.fuel_account.length})</Button>
      </div>

      {tab === "expenses" ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead><TableHead>Paid to / bill</TableHead><TableHead>Activity</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Paid from</TableHead><TableHead>By</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {data.expenses.length === 0 && <TableRow><TableCell colSpan={9} className="py-6 text-center text-sm text-muted-foreground">No site expenses yet.</TableCell></TableRow>}
              {data.expenses.map((e) => (
                <TableRow key={e.id} className={e.status === "CANCELLED" ? "opacity-50" : ""} data-testid={`site-expense-row-${e.id}`}>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDate(e.expense_date)}</TableCell>
                  <TableCell className="text-xs">{e.category_label}</TableCell>
                  <TableCell className="max-w-xs text-sm text-foreground">{e.description}{e.status === "CANCELLED" && <div className="text-[11px] text-rose-500">Cancelled: {e.cancel_reason}</div>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.paid_to || "—"}{e.bill_no ? <div>Bill {e.bill_no}</div> : null}</TableCell>
                  <TableCell className="max-w-[180px] text-xs text-muted-foreground" data-testid={`site-expense-activity-${e.id}`}>{activityLabel(activities, e.wbs_item_id)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    <div className="font-semibold text-foreground">{inr(e.total)}</div>
                    {Number(e.gst_amount) > 0 && <div className="text-[10px] text-muted-foreground">incl. GST {inr(e.gst_amount)}</div>}
                  </TableCell>
                  <TableCell className="text-xs">{e.paid_from === "PETTY_CASH" ? "Petty cash" : (e.bank_account || "Bank")}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.entered_by || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button size="sm" variant="ghost" title="Print / PDF" onClick={() => openPdf(`/print/site-expenses/${e.id}`)} data-testid={`print-site-expense-${e.id}`}><Printer className="h-3.5 w-3.5" /></Button>
                    {e.has_attachment && <Button size="sm" variant="ghost" title="Bill photo" onClick={() => openAttachment(`/projects/${projectId}/site-expenses/${e.id}/attachment`)}><Paperclip className="h-3.5 w-3.5" /></Button>}
                    {e.status === "POSTED" && data.can_enter && <Button size="sm" variant="ghost" title="Assign to an activity" onClick={() => setAssigning(e)} data-testid={`assign-activity-${e.id}`}><ListTree className="h-3.5 w-3.5" /></Button>}
                    {e.status === "POSTED" && data.can_enter && <Button size="sm" variant="ghost" title="Cancel" onClick={() => cancelExpense(e)}><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : tab === "petty" ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Holder</TableHead><TableHead>Reference</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>By</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {data.petty_cash.length === 0 && <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No petty cash given to this site yet.</TableCell></TableRow>}
              {data.petty_cash.map((p) => (
                <TableRow key={p.id} className={p.status === "CANCELLED" ? "opacity-50" : ""}>
                  <TableCell className="text-xs">{fmtDate(p.entry_date)}</TableCell>
                  <TableCell className="text-xs">{p.kind === "TOPUP" ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><ArrowDownLeft className="h-3.5 w-3.5" /> Given to site</span> : <span className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400"><ArrowUpRight className="h-3.5 w-3.5" /> Returned</span>}{p.status === "CANCELLED" && <span className="ml-1 text-rose-500">(cancelled)</span>}</TableCell>
                  <TableCell className="text-sm">{p.holder || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.reference || "—"}{p.bank_account ? <div>{p.kind === "TOPUP" ? "From" : "To"}: {p.bank_account}</div> : null}{p.remarks ? <div>{p.remarks}</div> : null}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{inr(p.amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.entered_by || "—"}</TableCell>
                  <TableCell className="text-right">{p.status === "POSTED" && data.can_manage && <Button size="sm" variant="ghost" onClick={() => cancelPetty(p)}><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : tab === "requests" ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Requested by</TableHead><TableHead>Purpose / recipient</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {cashRequests.length === 0 && <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No petty cash requests yet.</TableCell></TableRow>}
              {cashRequests.map((request) => (
                <TableRow key={request.id} data-testid={`petty-cash-request-${request.id}`}>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDate(request.requested_date)}</TableCell>
                  <TableCell className="text-xs">{request.requested_by || "—"}</TableCell>
                  <TableCell className="max-w-sm text-xs"><div className="font-medium text-foreground">{request.purpose}</div><div className="text-muted-foreground">For {request.recipient}</div>{request.review_note && <div className="text-muted-foreground">Note: {request.review_note}</div>}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{inr(request.requested_amount)}</TableCell>
                  <TableCell className="text-xs font-medium">{request.status}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {request.status === "PENDING" && cashRequestData.can_approve && <>
                      <Button size="sm" variant="outline" onClick={() => reviewCashRequest(request, "approve")}>Approve</Button>{" "}
                      <Button size="sm" variant="ghost" onClick={() => reviewCashRequest(request, "reject")}>Reject</Button>
                    </>}
                    {request.status === "APPROVED" && cashRequestData.can_issue &&
                      <Button size="sm" variant="outline" onClick={() => setIssuingRequest(request)}>Issue cash</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Fuel station</TableHead><TableHead>Reference</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>By</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {data.fuel_account.length === 0 && <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No Fuel Account top-ups yet.</TableCell></TableRow>}
              {data.fuel_account.map((f) => (
                <TableRow key={f.id} className={f.status === "CANCELLED" ? "opacity-50" : ""} data-testid={`fuel-account-row-${f.id}`}>
                  <TableCell className="text-xs">{fmtDate(f.entry_date)}</TableCell>
                  <TableCell className="text-xs">{f.kind === "TOPUP" ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><ArrowDownLeft className="h-3.5 w-3.5" /> Topped up</span> : <span className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400"><ArrowUpRight className="h-3.5 w-3.5" /> Returned</span>}{f.status === "CANCELLED" && <span className="ml-1 text-rose-500">(cancelled)</span>}</TableCell>
                  <TableCell className="text-sm">{f.fuel_station || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{f.reference || "—"}{f.bank_account ? <div>{f.kind === "TOPUP" ? "From" : "To"}: {f.bank_account}</div> : null}{f.remarks ? <div>{f.remarks}</div> : null}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{inr(f.amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{f.entered_by || "—"}</TableCell>
                  <TableCell className="text-right">{f.status === "POSTED" && data.can_manage && <Button size="sm" variant="ghost" onClick={() => cancelFuel(f)}><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <PettyCashIssueDialog projectId={projectId} companyId={data.company_id} request={issuingRequest} onClose={() => setIssuingRequest(null)} onSaved={() => { setIssuingRequest(null); load(); }} />
      <AssignActivityDialog expense={assigning} projectId={projectId} activities={activities} onClose={() => setAssigning(null)} onDone={() => { setAssigning(null); load(); }} />
    </div>
  );
}

export function ExpenseDialog({ projectId, companyId, categories, activities, balance, onSaved }) {
  const EMPTY = { expense_date: today(), category: "DIESEL", description: "", paid_to: "", bill_no: "", amount: "", gst_amount: "0", paid_from: "PETTY_CASH", wbs_item_id: "", bank_account_id: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const fileRef = useRef(null);
  const formRef = useRef(null);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const resetDraft = () => {
    setForm({ ...EMPTY });
    setSelectedBill(null);
    if (fileRef.current) fileRef.current.value = "";
    formRef.current?.reset();
  };
  const handleOpenChange = (nextOpen) => {
    resetDraft();
    setOpen(nextOpen);
  };
  const removeBill = () => {
    if (fileRef.current) fileRef.current.value = "";
    setSelectedBill(null);
  };
  const total = Number(form.amount || 0) + Number(form.gst_amount || 0);
  const tooMuch = form.paid_from === "PETTY_CASH" && total > balance;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/site-expenses`, {
        ...form, paid_to: form.paid_to || null, bill_no: form.bill_no || null, gst_amount: form.gst_amount || "0", wbs_item_id: form.wbs_item_id || null, bank_account_id: form.paid_from === "BANK" ? form.bank_account_id || null : null,
      });
      const file = fileRef.current?.files?.[0];
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try { await api.post(`/projects/${projectId}/site-expenses/${data.id}/attachment`, fd); }
        catch (err) { toast.warning(`Saved, but the bill photo failed: ${getErrorMessage(err)}`); }
      }
      toast.success("Expense recorded — project cost and accounts updated");
      handleOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild><Button data-testid="add-site-expense-button"><Plus className="h-4 w-4" /> Add expense</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New site expense</DialogTitle></DialogHeader>
        <form ref={formRef} onSubmit={save} className="space-y-3.5" data-testid="site-expense-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" value={form.expense_date} onChange={set("expense_date")} className="mt-1.5" data-testid="site-expense-date" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="site-expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Input required value={form.description} onChange={set("description")} placeholder="e.g. Diesel 100 L for JCB & crane" className="mt-1.5" data-testid="site-expense-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Paid to</Label><Input value={form.paid_to} onChange={set("paid_to")} placeholder="Indian Oil pump, Arakalgudu" className="mt-1.5" /></div>
            <div><Label>Bill no.</Label><Input value={form.bill_no} onChange={set("bill_no")} className="mt-1.5" /></div>
            <div><Label>Amount before GST (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="site-expense-amount" /></div>
            <div><Label>GST on the bill (₹)</Label><Input type="number" step="0.01" min="0" value={form.gst_amount} onChange={set("gst_amount")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Paid from</Label>
            <Select value={form.paid_from} onValueChange={(v) => setForm({ ...form, paid_from: v })}>
              <SelectTrigger className="mt-1.5" data-testid="site-expense-paid-from"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PETTY_CASH">Site petty cash (balance {inr(balance)})</SelectItem>
                <SelectItem value="BANK">Company bank / UPI</SelectItem>
              </SelectContent>
            </Select>
            {tooMuch && <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">More than the petty cash balance — ask for a top-up, or choose Company bank.</p>}
          </div>
          {form.paid_from === "BANK" && <BankAccountSelect companyId={companyId} value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} label="Which bank / cash account" testId="site-expense-bank-select" />}
          <div><Label>Activity (optional)</Label><ActivitySelect value={form.wbs_item_id} onChange={(v) => setForm({ ...form, wbs_item_id: v })} activities={activities} /></div>
          <div>
            <Label>Bill photo (optional)</Label>
            <Input ref={fileRef} type="file" accept="image/*,.pdf" onChange={(e) => setSelectedBill(e.target.files?.[0] || null)} className="mt-1.5" data-testid="site-expense-bill-input" />
            {selectedBill && (
              <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground" data-testid="site-expense-selected-bill">
                <span className="truncate">{selectedBill.name}</span>
                <Button type="button" size="sm" variant="ghost" onClick={removeBill} aria-label="Remove selected bill" data-testid="site-expense-remove-bill">
                  <X className="h-3.5 w-3.5" /> Remove
                </Button>
              </div>
            )}
          </div>
          <div className="rounded-lg bg-muted/50 p-2.5 text-sm"><div className="flex justify-between font-semibold"><span>Total paid</span><span className="font-mono">{inr(total)}</span></div></div>
          <DialogFooter><Button type="submit" disabled={saving || tooMuch}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save expense</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PettyCashDialog({ projectId, companyId, balance, canIssue, onSaved }) {
  const EMPTY = { entry_date: today(), kind: "TOPUP", amount: "", holder: "", bank_account_id: "", reference: "", remarks: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(canIssue ? EMPTY : { ...EMPTY, kind: "RETURN" });
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/petty-cash`, { ...form, holder: form.holder || null, bank_account_id: form.bank_account_id || null, reference: form.reference || null, remarks: form.remarks || null });
      toast.success(form.kind === "TOPUP" ? "Petty cash given to the site" : "Petty cash returned to the bank");
      setForm(canIssue ? EMPTY : { ...EMPTY, kind: "RETURN" });
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="petty-cash-button"><Wallet className="h-4 w-4" /> Petty cash</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Petty cash — balance {inr(balance)}</DialogTitle>
          <DialogDescription>Give money to the site in-charge from the bank, or record unused cash coming back.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              {canIssue ? <>
                <Label>Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="TOPUP">Give to site (top-up)</SelectItem><SelectItem value="RETURN">Returned to office</SelectItem></SelectContent>
                </Select>
              </> : <><Label>Type</Label><p className="mt-2 text-sm text-muted-foreground">Returned to office</p></>}
            </div>
            <div><Label>Date</Label><Input required type="date" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
            <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="petty-amount" /></div>
            <div><Label>Given to (site in-charge)</Label><Input value={form.holder} onChange={set("holder")} className="mt-1.5" /></div>
          </div>
          <BankAccountSelect companyId={companyId} value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} label={form.kind === "TOPUP" ? "Given from (bank / cash account)" : "Returned to (bank / cash account)"} testId="petty-bank-select" />
          <div><Label>UTR / cheque no.</Label><Input value={form.reference} onChange={set("reference")} className="mt-1.5" /></div>
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PettyCashRequestDialog({ projectId, onSaved }) {
  const EMPTY = { requested_date: today(), requested_amount: "", recipient: "", purpose: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/petty-cash-requests`, {
        ...form,
        requested_amount: form.requested_amount,
        recipient: form.recipient.trim(),
        purpose: form.purpose.trim(),
      });
      toast.success("Petty cash request sent to Accounts and Admin");
      setForm(EMPTY);
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) setForm(EMPTY); }}>
      <DialogTrigger asChild><Button variant="outline" data-testid="raise-petty-cash-request"><Wallet className="h-4 w-4" /> Request petty cash</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request petty cash</DialogTitle>
          <DialogDescription>Accounts or Admin must approve the request and record the cash when it is issued.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div><Label>Request date</Label><Input required type="date" value={form.requested_date} onChange={set("requested_date")} className="mt-1.5" /></div>
          <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.requested_amount} onChange={set("requested_amount")} className="mt-1.5" /></div>
          <div><Label>Cash recipient</Label><Input required maxLength={200} value={form.recipient} onChange={set("recipient")} className="mt-1.5" placeholder="Site engineer / project manager / site manager" /></div>
          <div><Label>Purpose</Label><Input required maxLength={1000} value={form.purpose} onChange={set("purpose")} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Submit request</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PettyCashIssueDialog({ projectId, companyId, request, onClose, onSaved }) {
  const EMPTY = { entry_date: today(), amount: "", holder: "", bank_account_id: "", reference: "", remarks: "" };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(request ? { ...EMPTY, amount: request.requested_amount, holder: request.recipient } : EMPTY);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);
  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const save = async (event) => {
    event.preventDefault();
    if (!request) return;
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/petty-cash-requests/${request.id}/issue`, {
        ...form,
        holder: form.holder.trim(),
        bank_account_id: form.bank_account_id || null,
        reference: form.reference || null,
        remarks: form.remarks || null,
      });
      toast.success("Cash issuance recorded; petty cash request completed");
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!request} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Issue petty cash</DialogTitle>
          <DialogDescription>Approved request: {request?.purpose} — requested {inr(request?.requested_amount)}.</DialogDescription>
        </DialogHeader>
        {request && <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Issue date</Label><Input required type="date" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
            <div><Label>Amount issued (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" /></div>
          </div>
          <div><Label>Given to</Label><Input required maxLength={200} value={form.holder} onChange={set("holder")} className="mt-1.5" /></div>
          <BankAccountSelect companyId={companyId} value={form.bank_account_id} onChange={(value) => setForm({ ...form, bank_account_id: value })} label="Given from (bank / cash account)" testId="petty-request-bank-select" />
          <div><Label>UTR / cheque no.</Label><Input value={form.reference} onChange={set("reference")} className="mt-1.5" /></div>
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Record issue</Button></DialogFooter>
        </form>}
      </DialogContent>
    </Dialog>
  );
}

function FuelAccountDialog({ projectId, companyId, balance, onSaved }) {
  const EMPTY = { entry_date: today(), kind: "TOPUP", amount: "", fuel_station: "", bank_account_id: "", reference: "", remarks: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/fuel-account`, { ...form, fuel_station: form.fuel_station || null, bank_account_id: form.bank_account_id || null, reference: form.reference || null, remarks: form.remarks || null });
      toast.success(form.kind === "TOPUP" ? "Fuel Account topped up" : "Fuel Account refund recorded");
      setForm(EMPTY);
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="fuel-account-button"><Fuel className="h-4 w-4" /> Fuel Account</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Fuel Account — balance {inr(balance)}</DialogTitle>
          <DialogDescription>Record paying the fuel station in advance, or unused fuel credit coming back.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="TOPUP">Pay fuel station (top-up)</SelectItem><SelectItem value="RETURN">Refund to office</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
            <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="fuel-account-amount" /></div>
            <div><Label>Fuel station</Label><Input value={form.fuel_station} onChange={set("fuel_station")} className="mt-1.5" /></div>
          </div>
          <BankAccountSelect companyId={companyId} value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} label={form.kind === "TOPUP" ? "Paid from (bank / cash account)" : "Refunded to (bank / cash account)"} testId="fuel-bank-select" />
          <div><Label>Reference no.</Label><Input value={form.reference} onChange={set("reference")} className="mt-1.5" /></div>
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="fuel-account-submit">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
